import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
// Constants
const DUAL_SIGNATURE_IMAGE_URL = 'https://blobaneesh.blob.core.windows.net/images/appraisals/sig.bmp';
const DIAMOND_LOGO_URL = 'https://blobaneesh.blob.core.windows.net/images/appraisals/diamondvector.png';
// PDF Styles
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 11,
        fontFamily: 'Times-Roman',
        color: '#000',
        backgroundColor: '#fff'
    },
    logo: {
        width: 80,
        marginBottom: 20,
        alignSelf: 'center'
    },
    header: {
        textAlign: 'center',
        marginBottom: 30,
        paddingBottom: 15,
        borderBottom: '3px solid #000'
    },
    companyName: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 4
    },
    companyInfo: {
        fontSize: 10,
        color: '#666',
        marginBottom: 8
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        marginTop: 10,
        marginBottom: 10
    },
    propertyInfo: {
        fontSize: 11,
        color: '#666',
        marginTop: 10
    },
    section: {
        marginBottom: 20
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        paddingBottom: 5,
        borderBottom: '2px solid #ddd'
    },
    row: {
        flexDirection: 'row',
        marginBottom: 6
    },
    label: {
        width: '40%',
        fontWeight: 'bold',
        fontSize: 11
    },
    value: {
        width: '60%',
        fontSize: 11
    },
    table: {
        marginTop: 10,
        borderWidth: 1,
        borderColor: '#ddd'
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
        padding: 8,
        fontWeight: 'bold'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
        padding: 8
    },
    tableCell: {
        flex: 1,
        fontSize: 10
    },
    photoGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 10
    },
    photo: {
        width: '30%',
        height: 120,
        objectFit: 'contain',
        border: '1px solid #ddd'
    },
    total: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'right',
        marginTop: 30,
        paddingTop: 15,
        borderTop: '3px solid #000'
    },
    signatureSection: {
        marginTop: 30,
        paddingTop: 15,
        borderTop: '2px solid #ddd'
    },
    signatureLabel: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 10
    },
    signatureImage: {
        objectFit: 'scale-down',
        alignSelf: 'center',
        marginBottom: 10
    },
    signatureGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 10
    },
    signatureColumn: {
        width: '48%'
    },
    signatureText: {
        fontSize: 10,
        marginBottom: 2
    },
    footer: {
        marginTop: 30,
        paddingTop: 15,
        borderTop: '2px solid #ddd',
        fontSize: 10,
        color: '#666',
        lineHeight: 1.5
    },
    footerTitle: {
        fontWeight: 'bold',
        fontSize: 11,
        marginBottom: 8,
        letterSpacing: 0.5
    },
    footerText: {
        marginBottom: 8,
        lineHeight: 1.6,
        textAlign: 'justify'
    }
});
export const AppraisalPDFDocument = ({ mode, formData, gemstones, photos, customFieldsStep1, customFieldsStep2, customFieldsStep5, selectedAppraisal, signatureImageBase64, logoImageBase64 }) => {
    const customerName = selectedAppraisal?.customerName || formData.customerName || 'Customer Name';
    const appraisalNumber = selectedAppraisal?.appraisalNumber || 'APR-2025-NEW';
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
    // Use base64 images if provided, otherwise fall back to URLs
    const logoSrc = logoImageBase64 || DIAMOND_LOGO_URL;
    const signatureSrc = signatureImageBase64 || DUAL_SIGNATURE_IMAGE_URL;
    return (_jsx(Document, { children: _jsxs(Page, { size: "LETTER", style: styles.page, children: [_jsx(Image, { src: logoSrc, style: styles.logo }), _jsxs(View, { style: styles.header, children: [_jsx(Text, { style: styles.companyName, children: mode === 'test' ? (formData.companyName || 'ANEESH JAIN, LLC.') : 'ROYAL GEMS, INC.' }), _jsx(Text, { style: styles.companyInfo, children: mode === 'test'
                                ? `${formData.companyAddress || '525 Vine St, 23rd floor, Cincinnati, OH 45202'}\nPhone: ${formData.companyPhone || '513-349-8816'}`
                                : 'Certified Gemologists & Appraisers\n123 Diamond District, New York, NY 10036\nPhone: (555) 123-4567' }), _jsx(Text, { style: styles.title, children: "APPRAISAL FOR INSURANCE" }), _jsxs(Text, { style: styles.propertyInfo, children: ["Appraisal Number: ", appraisalNumber, '\n', "Date of Appraisal: ", selectedAppraisal?.date ? new Date(selectedAppraisal.date).toLocaleDateString() : new Date().toLocaleDateString(), mode === 'test' && `\n\nThe item described below is the property of:\n${customerName}\n${formData.customerAddress || ''}`] })] }), mode === 'test' && formData.itemDescription && (_jsxs(View, { style: styles.section, wrap: false, children: [_jsx(Text, { style: styles.sectionTitle, children: "Complete Item Description" }), _jsx(Text, { style: { fontSize: 11, lineHeight: 1.6, textAlign: 'justify', width: '100%' }, children: formData.itemDescription })] })), _jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.sectionTitle, children: "Customer Information" }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Name:" }), _jsx(Text, { style: styles.value, children: customerName })] }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Email:" }), _jsx(Text, { style: styles.value, children: selectedAppraisal?.customerEmail || formData.customerEmail || 'N/A' })] }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Phone:" }), _jsx(Text, { style: styles.value, children: selectedAppraisal?.customerPhone || formData.customerPhone || 'N/A' })] }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Appraisal Date:" }), _jsx(Text, { style: styles.value, children: selectedAppraisal?.date ? new Date(selectedAppraisal.date).toLocaleDateString() : new Date().toLocaleDateString() })] }), mode === 'test' && customFieldsStep1.map((field) => (_jsxs(View, { style: styles.row, children: [_jsxs(Text, { style: styles.label, children: [field.fieldName, ":"] }), _jsx(Text, { style: styles.value, children: field.fieldValue })] }, field.id)))] }), _jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.sectionTitle, children: "Item Description" }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Type:" }), _jsx(Text, { style: styles.value, children: formData.jewelryType || 'N/A' })] }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Metal Type:" }), _jsx(Text, { style: styles.value, children: formData.metalType || 'N/A' })] }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Setting Description:" }), _jsx(Text, { style: styles.value, children: formData.settingDescription || 'N/A' })] }), _jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Condition:" }), _jsx(Text, { style: styles.value, children: formData.condition || 'Excellent' })] }), mode === 'test' && customFieldsStep2.map((field) => (_jsxs(View, { style: styles.row, children: [_jsxs(Text, { style: styles.label, children: [field.fieldName, ":"] }), _jsx(Text, { style: styles.value, children: field.fieldValue })] }, field.id)))] }), gemstones.length > 0 && (_jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.sectionTitle, children: "Gemstone Details" }), _jsxs(View, { style: styles.table, children: [_jsxs(View, { style: styles.tableHeader, children: [_jsx(Text, { style: { ...styles.tableCell, width: '8%' }, children: "#" }), _jsx(Text, { style: { ...styles.tableCell, width: '18%' }, children: "Species" }), _jsx(Text, { style: { ...styles.tableCell, width: '20%' }, children: "Cut" }), _jsx(Text, { style: { ...styles.tableCell, width: '12%' }, children: "Carat" }), _jsx(Text, { style: { ...styles.tableCell, width: '12%' }, children: "Color" }), _jsx(Text, { style: { ...styles.tableCell, width: '12%' }, children: "Clarity" }), _jsx(Text, { style: { ...styles.tableCell, width: '18%' }, children: "Value" })] }), gemstones.map((gem, index) => (_jsxs(View, { style: styles.tableRow, children: [_jsx(Text, { style: { ...styles.tableCell, width: '8%' }, children: index + 1 }), _jsx(Text, { style: { ...styles.tableCell, width: '18%' }, children: gem.species }), _jsx(Text, { style: { ...styles.tableCell, width: '20%' }, children: gem.cut }), _jsxs(Text, { style: { ...styles.tableCell, width: '12%' }, children: [gem.carat, "ct"] }), _jsx(Text, { style: { ...styles.tableCell, width: '12%' }, children: gem.color }), _jsx(Text, { style: { ...styles.tableCell, width: '12%' }, children: gem.clarity }), _jsxs(Text, { style: { ...styles.tableCell, width: '18%' }, children: ["$", parseFloat(gem.value || '0').toLocaleString()] })] }, gem.id)))] })] })), photos.length > 0 && (_jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.sectionTitle, children: "Item Photography" }), _jsx(View, { style: styles.photoGrid, children: photos.slice(0, 6).map((photo, index) => (_jsx(Image, { src: photo, style: styles.photo }, index))) })] })), mode === 'test' && customFieldsStep5.length > 0 && (_jsx(View, { style: { marginTop: 15, marginBottom: 15 }, children: customFieldsStep5.map((field) => (_jsxs(View, { style: styles.row, children: [_jsxs(Text, { style: styles.label, children: [field.fieldName, ":"] }), _jsx(Text, { style: styles.value, children: field.fieldValue })] }, field.id))) })), mode === 'test' && (formData.diamondRingValue || formData.goldRingValue || formData.goldPriceReference) && (_jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.sectionTitle, children: "Valuation Breakdown" }), formData.diamondRingValue && (_jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Diamond Replacement Value:" }), _jsxs(Text, { style: styles.value, children: ["$", parseInt(formData.diamondRingValue).toLocaleString(), " USD"] })] })), formData.goldRingValue && (_jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Ring Replacement Value:" }), _jsxs(Text, { style: styles.value, children: ["$", parseInt(formData.goldRingValue).toLocaleString(), " USD"] })] })), formData.goldPriceReference && (_jsxs(View, { style: styles.row, children: [_jsx(Text, { style: styles.label, children: "Metal Price Reference:" }), _jsx(Text, { style: styles.value, children: formData.goldPriceReference })] }))] })), _jsx(View, { style: styles.total, children: _jsxs(Text, { children: ["TOTAL APPRAISED VALUE: $", parseInt(formData.totalValue || '0').toLocaleString(), " USD"] }) }), _jsx(View, { style: styles.signatureSection, children: mode === 'test' ? (_jsxs(_Fragment, { children: [_jsx(Image, { src: signatureSrc, style: styles.signatureImage }), _jsx(View, { style: styles.signatureGrid, children: _jsx(View, { style: styles.signatureColumn, children: _jsxs(Text, { style: { ...styles.signatureText, fontWeight: 'bold' }, children: ["DATE: ", today] }) }) })] })) : (_jsxs(View, { style: styles.signatureGrid, children: [_jsxs(View, { style: styles.signatureColumn, children: [_jsx(Text, { style: styles.signatureLabel, children: "Appraiser Signature:" }), _jsx(Text, { style: { fontSize: 20, fontFamily: 'Times-Italic', marginBottom: 5 }, children: "John Smith" }), _jsx(Text, { style: styles.signatureText, children: "John Smith, GG (Graduate Gemologist)" }), _jsx(Text, { style: styles.signatureText, children: "GIA License #12345678" })] }), _jsxs(View, { style: styles.signatureColumn, children: [_jsx(Text, { style: styles.signatureLabel, children: "Date:" }), _jsx(Text, { style: { fontSize: 14, marginBottom: 5 }, children: new Date().toLocaleDateString() })] })] })) })] }) }));
};
